import React, { ChangeEvent, useEffect, useState } from 'react'
import { AppDispatch, RootState } from '../../../reducers/store';
import { useDispatch, useSelector } from 'react-redux';
import { postValidateSelectedFile, postValidateCategory, postValidateTitle, postValidateLink, postValidateDescription } from '../../../components/validations/posts/createPost';
import { actionCreatePost } from '../../../actions/posts';
import { Button, FormField, ImageBuffer, Loader, SubCover, SubmitModal } from '../../../components';
import { create } from '../../../assets';
import { allCategoryHeader, PhotoRounded } from '../../../utils/constants';
import toast from 'react-hot-toast';

import './createPost.css'

interface PostDataParams {
    selectedFile?: File | null;
    title?: string;
    description?: string;
    category?: string;
    link?: string;
}

const CreatePost = () => {
    const dispatch                                    = useDispatch<AppDispatch>();
    const [fileNames, setFileNames]                   = useState<{ [key: string]: string }>({});
    const [savingInfo, setSavingInfo]                 = useState<boolean>(false);
    const [isButtonDisabled, setIsButtonDisabled]     = useState<boolean>(true);
    const [onOpen, setOnOpen]                         = useState<boolean>(false);
    const [openSubMegaMenu, setOpenSubMegaMenu]       = useState<boolean>(false);
    const [userIdErrors, setUserIdErrors]             = useState<string[]>([]);
    const [productImg, setProductImg]                 = useState<string>();
    const [selectedFileErrors, setSelectedFileErrors] = useState<string[]>([]);
    const [titleErrors, setTitleErrors]               = useState<string[]>([]);
    const [descriptionErrors, setDescriptionErrors]   = useState<string[]>([]);
    const [categoryErrors, setCategoryErrors]         = useState<string[]>([]);
    const [linkErrors, setLinkErrors]                 = useState<string[]>([]);
    const [errorMessage, setErrorMessage]             = useState(null);

    const [postData, setPostData]             = useState<PostDataParams>({ selectedFile: null, title: '', description: '', category: '', link: '' })

    const [ isLoadingBtn, setIsLoadingBtn ]   = useState({selectedFile: false, title: false, description: false, category: false, link: false})

    const [ isValid, setIsValid ]             = useState({selectedFile: false, title: false, description: false, category: false, link: false})

    const useDebounce = <T,>(value: T, delay: number): T => {
        const [debounced, setDebounced] = useState(value);
    
        useEffect(() => {
        const handler = setTimeout(() => {
            setDebounced(value);
        }, delay);
        return () => clearTimeout(handler);
        }, [value, delay]);
    
        return debounced;
    };

    const debouncedPostData = useDebounce<PostDataParams>(postData, 500);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setIsLoadingBtn((prevState) => ({ ...prevState, [name]: true }));
        setPostData((prevState) => ({ ...prevState, [name]: value }));
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileNames(prev => ({
            ...prev,
            [e.target.name]: file.name,
        }));

        // Store RAW file
        setPostData(prev => ({
        ...prev,
        selectedFile: file,
        }));

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setProductImg(previewUrl);
    };

    const handleProductImageUpload = (e: any) => {
        const file = e.target.files[0]

        transformFile(file)
    }

    const transformFile = (file: File | null) => {
        const reader = new FileReader();
      
        if (file) {
          reader.readAsDataURL(file);
      
          reader.onloadend = () => {
            const result = reader.result;
      
            // Ensure the result is a string before updating state
            if (typeof result === 'string') {
              setProductImg(result);
            }
          };
        } else {
          setProductImg(''); // Reset the array if no file is passed
        }
    };

    useEffect(() => {
        const validateSelectedFile = () => {
            const { isValid, errors } = postValidateSelectedFile({ postData: debouncedPostData });
            setIsValid((prevState) => ({
                ...prevState,
                selectedFile: isValid,
            }));
            setSelectedFileErrors(errors);
            setIsLoadingBtn((prevState) => ({
                ...prevState,
                selectedFile: false,
            }));
            return errors;
        };

        const validateTitle = () => {
            const { isValid, errors } = postValidateTitle({ postData: debouncedPostData });
            setIsValid((prevState) => ({
                ...prevState,
                title: isValid,
            }));
            setTitleErrors(errors);
            setIsLoadingBtn((prevState) => ({
                ...prevState,
                title: false,
            }));
            return errors;
        }

        const validateDescription = () => {
            const { isValid, errors } = postValidateDescription({ postData: debouncedPostData });
            setIsValid((prevState) => ({
                ...prevState,
                description: isValid,
            }));
            setDescriptionErrors(errors);
            setIsLoadingBtn((prevState) => ({
                ...prevState,
                description: false,
            }));
            return errors;
        }

        const validateCategory = () => {
            const { isValid, errors } = postValidateCategory({ postData: debouncedPostData });
            setIsValid((prevState) => ({
                ...prevState,
                category: isValid,
            }));
            setCategoryErrors(errors);
            setIsLoadingBtn((prevState) => ({
                ...prevState,
                category: false,
            }));
            return errors;
        }

        const validateLink = () => {
            const { isValid, errors } = postValidateLink({ postData: debouncedPostData });
            setIsValid((prevState) => ({
                ...prevState,
                link: isValid,
            }));
            setLinkErrors(errors);
            setIsLoadingBtn((prevState) => ({
                ...prevState,
                link: false,
            }));
            return errors;
        }

        const selectedFileErrors  = validateSelectedFile();
        const titleErrors         = validateTitle();
        const descriptionErrors   = validateDescription();
        const categoryErrors      = validateCategory();
        const linkErrors          = validateLink();

        setUserIdErrors(userIdErrors)
        setSelectedFileErrors(selectedFileErrors)
        setTitleErrors(titleErrors)
        setDescriptionErrors(descriptionErrors)
        setCategoryErrors(categoryErrors)
        setLinkErrors(linkErrors)

        const hasFormErrors = selectedFileErrors.length > 0 || titleErrors.length > 0 || descriptionErrors.length > 0 || categoryErrors.length > 0 || linkErrors.length > 0;
        setIsButtonDisabled(hasFormErrors);

    }, [debouncedPostData])

    const handleModalSubmit = () => {
        setOnOpen(true);
        setOpenSubMegaMenu(true);
    }

    const handleCloseCover = () => {
        setOnOpen(false) // modal
        setOpenSubMegaMenu(false)
    }

    const handleSubmit = async(e: ChangeEvent<any>) => {
        e.preventDefault();
        handleModalSubmit()
    }

    const cancelPostCreation = () => {
        setOnOpen(false); // Close the modal
        setSavingInfo(false);
        setOpenSubMegaMenu(false)
    };

    const confirmPostCreation = async() => {
        setSavingInfo(true);
        setIsButtonDisabled(true);
        const formData = new FormData();

        Object.entries(postData).forEach(([key, value]) => {
            if (value !== null) {
                formData.append(key, value as any);
            }
        });
        const res = await dispatch(actionCreatePost({postData: formData}))
        try{
            if (res.success === true) {
            // Password reset successful, show success message from response
            toast.success("post created successfully");
            setOpenSubMegaMenu(false)
            setErrorMessage(null); // Clear any previous error message
            setPostData({ selectedFile: null, title: '', description: '', category: '', link: '' })
            setSavingInfo(false)
            setProductImg('')
            setFileNames({});
            } else if(res.status === 400) {
                setErrorMessage(res?.data.message);
                setSavingInfo(false)
                setOpenSubMegaMenu(false)
            }
        }catch(error){
            setErrorMessage(res.status)
            setSavingInfo(false)
            setOpenSubMegaMenu(false)
        }
        setOnOpen(false); // Close the modal after confirmation
    };

  return (
    <>
        <title>Create Post</title> 
        <div className='account-container'>
            <div className='account-reg-container'>
                <div className='create-post-wrapper-title'>
                    <h3>Create Post</h3>
                </div>
            
                <div className='create-post-form-wrapper'>
                    <form className='create-post-form' onSubmit={handleSubmit} autoComplete="off">
                        <div className='reg-image-group'>
                            <div onChange={handleProductImageUpload}>
                                <FormField fileInputType name="selectedFile" selectedLabelName="Upload Photo" accept="image/*" handleChange={handleImageUpload} selectedFileName={fileNames.selectedFile} isLoadingBtn={isLoadingBtn.selectedFile} isValid={isValid.selectedFile} errors={selectedFileErrors || []} />
                            </div>
                            <div className='reg-image-preview-wrapper'>
                                {productImg ? <ImageBuffer src={productImg} alt='productImg' />  : <span><PhotoRounded /></span>}
                            </div>
                        </div>

                        <FormField inputType type="text" labelName="Title" name="title" value={postData.title} handleChange={handleChange} isLoadingBtn={isLoadingBtn.title} isValid={isValid.title} errors={titleErrors || []} />

                        <FormField inputType type="text" labelName="Description" name="description" value={postData.description} handleChange={handleChange} isLoadingBtn={isLoadingBtn.description} isValid={isValid.description} maxLength={120} errors={descriptionErrors || []} />

                        <FormField selectType renderOption={(option) => ({ value: option.hint, label: `${option.title}` })} labelName="Category" name="category" value={postData.category} handleChange={handleChange} options={allCategoryHeader} isLoadingBtn={isLoadingBtn.category} isValid={isValid.category} errors={categoryErrors || []} />

                        <FormField inputType type="text" labelName="Link" name="link" value={postData.link} handleChange={handleChange} isLoadingBtn={isLoadingBtn.link} isValid={isValid.link} errors={linkErrors || []} />

                        <SubmitModal onOpen={onOpen} onClose={cancelPostCreation} onConfirm={confirmPostCreation} modalImage={create} prompt="Are you sure you want to create post" />
                        <Button onClickButton buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} Posting...</> : "Create"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />
                        {errorMessage && <p className='error-msg'>{errorMessage}</p>}
                    </form>
                </div>
            </div>
        </div>

        {openSubMegaMenu && (
            //z-index: 3, others should be higher
            <SubCover handleCloseCover={handleCloseCover} />
        )}
    </>
  )
}

export default CreatePost